/**
 * Twin-Matrix AR reconstitution — zero OpenCV.
 * Finder scan (1:1:3:1:1) + 4-point homography warp + paint missing_modules.
 * Instant on mobile (no 10MB WASM).
 */

import {
  CELL,
  QUIET,
  SEARCH_MARGIN,
  applyMissingModules,
  buildSamplePlan,
  otsuThreshold,
  qrGeometry,
  symbolGeometry,
  sampleGridDark,
  searchModuleGrid,
  toGrayscale,
  voidBounds,
} from "./matrixGrid";

const FINDER_CENTER = 3.5; // modules from outer edge to finder center

function luminance(data, w, x, y) {
  const i = (y * w + x) * 4;
  return data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
}

function meanLuminance(imageData) {
  const { data, width: w, height: h } = imageData;
  let sum = 0;
  let n = 0;
  // subsample for speed
  for (let y = 0; y < h; y += 4) {
    for (let x = 0; x < w; x += 4) {
      sum += luminance(data, w, x, y);
      n++;
    }
  }
  return n ? sum / n : 128;
}

function isBlack(data, w, x, y, thr) {
  return luminance(data, w, x, y) < thr;
}

/** Check 1:1:3:1:1 run ratios (QR finder cross-section). */
function ratiosOk(runs) {
  if (runs.length < 5) return false;
  const [a, b, c, d, e] = runs;
  const unit = (a + b + c + d + e) / 7;
  if (unit < 1.0) return false;
  const near = (v, t, tol = 0.75) => Math.abs(v / unit - t) <= tol;
  return near(a, 1) && near(b, 1) && near(c, 3) && near(d, 1) && near(e, 1);
}

/** Downscale for finder scan (keeps aspect). Returns { imageData, scale }. */
function downscaleForScan(imageData, maxW = 640) {
  const { width: w, height: h } = imageData;
  if (w <= maxW) return { imageData, scale: 1 };
  const scale = maxW / w;
  const nw = Math.round(w * scale);
  const nh = Math.round(h * scale);
  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(nw, nh)
      : Object.assign(document.createElement("canvas"), { width: nw, height: nh });
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  // put full frame then scale draw via temporary
  const src =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement("canvas"), { width: w, height: h });
  src.getContext("2d").putImageData(imageData, 0, 0);
  ctx.drawImage(src, 0, 0, nw, nh);
  return { imageData: ctx.getImageData(0, 0, nw, nh), scale };
}

/**
 * Scan for finder pattern centers via B/W run ratios (horizontal + confirm vertical).
 * @returns {{x:number,y:number,size:number}[]}
 */
export function findFinderCenters(imageData, thr = null) {
  const { data, width: w, height: h } = imageData;
  const threshold = thr ?? Math.max(70, Math.min(140, meanLuminance(imageData) * 0.72));
  const hits = [];

  for (let y = 1; y < h - 1; y += 1) {
    let x = 0;
    while (x < w) {
      while (x < w && !isBlack(data, w, x, y, threshold)) x++;
      if (x >= w) break;
      const runStart = x;
      const runs = [];
      let dark = true;
      while (x < w && runs.length < 5) {
        const start = x;
        while (x < w && isBlack(data, w, x, y, threshold) === dark) x++;
        runs.push(x - start);
        dark = !dark;
      }
      if (runs.length === 5 && ratiosOk(runs)) {
        const total = runs.reduce((s, n) => s + n, 0);
        const cx = runStart + total / 2;
        const vx = Math.round(cx);
        if (vx > 0 && vx < w - 1) {
          const vruns = [];
          let yy = Math.round(y);
          while (yy > 0 && isBlack(data, w, vx, yy, threshold) === isBlack(data, w, vx, Math.round(y), threshold)) {
            yy--;
          }
          yy++;
          let darkV = isBlack(data, w, vx, yy, threshold);
          while (yy < h && vruns.length < 5) {
            const start = yy;
            while (yy < h && isBlack(data, w, vx, yy, threshold) === darkV) yy++;
            vruns.push(yy - start);
            darkV = !darkV;
          }
          if (vruns.length === 5 && ratiosOk(vruns)) {
            const vtotal = vruns.reduce((s, n) => s + n, 0);
            const cy = yy - vtotal + vtotal / 2;
            hits.push({ x: cx, y: cy, size: (total + vtotal) / 2 });
          }
        }
      }
      x = runStart + Math.max(1, runs[0] || 1);
    }
  }

  return { centers: clusterCenters(hits), thr: threshold };
}

function clusterCenters(hits) {
  const clusters = [];
  for (const h of hits) {
    let merged = false;
    for (const c of clusters) {
      const dx = c.x - h.x;
      const dy = c.y - h.y;
      if (dx * dx + dy * dy < (c.size * 0.6) ** 2) {
        c.x = (c.x * c.n + h.x) / (c.n + 1);
        c.y = (c.y * c.n + h.y) / (c.n + 1);
        c.size = (c.size * c.n + h.size) / (c.n + 1);
        c.n += 1;
        merged = true;
        break;
      }
    }
    if (!merged) clusters.push({ ...h, n: 1 });
  }
  return clusters.sort((a, b) => b.size - a.size).slice(0, 4);
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function sampleBrightness(imageData, x, y, r = 6) {
  const { data, width: w, height: h } = imageData;
  let sum = 0;
  let n = 0;
  const x0 = Math.max(0, Math.round(x) - r);
  const y0 = Math.max(0, Math.round(y) - r);
  const x1 = Math.min(w - 1, Math.round(x) + r);
  const y1 = Math.min(h - 1, Math.round(y) + r);
  for (let yy = y0; yy <= y1; yy++) {
    for (let xx = x0; xx <= x1; xx++) {
      sum += luminance(data, w, xx, yy);
      n++;
    }
  }
  return n ? sum / n : 0;
}

/**
 * Pick TL/BL among finders; infer TR via 90° rotation toward the cut void (top-right).
 *
 * Phone cameras inflate run-length "size", so geom is soft. Prefer leftmost vertical
 * pairs and the rotation that puts TR up-right into a bright void.
 * Important: never keep a NaN score — `NaN > x` is always false and freezes the best pick.
 */
export function resolveTlBl(imageData, centers, geometry) {
  if (!centers || centers.length < 2) return null;
  const idealGeom = geometry.finderSpan / 7;
  const primary = centers.slice(0, Math.min(6, centers.length));
  let best = null;
  const { width, height } = imageData;
  const margin = Math.max(8, Math.min(width, height) * 0.02);

  for (let i = 0; i < primary.length; i++) {
    for (let j = i + 1; j < primary.length; j++) {
      const a = primary[i];
      const b = primary[j];
      const d = dist(a, b);
      if (d < 24) continue;

      const sizeRatio = Math.min(a.size, b.size) / Math.max(a.size, b.size);
      if (sizeRatio < 0.3) continue;

      const avgSize = (a.size + b.size) / 2;
      const geom = d / Math.max(avgSize, 1);
      if (geom < 1.2 || geom > 14) continue;

      const dx = Math.abs(a.x - b.x);
      const dy = Math.abs(a.y - b.y);
      if (dy < d * 0.4) continue;
      if (dx > dy * 0.85) continue;

      for (const [tl, bl] of [
        [a, b],
        [b, a],
      ]) {
        const down = { x: bl.x - tl.x, y: bl.y - tl.y };
        if (down.y < d * 0.4) continue;

        for (const right of [
          { x: -down.y, y: down.x },
          { x: down.y, y: -down.x },
        ]) {
          // Void is top-right → TR must be to the right of TL.
          if (right.x < d * 0.25) continue;

          const tr = { x: tl.x + right.x, y: tl.y + right.y };
          const br = { x: bl.x + right.x, y: bl.y + right.y };
          const trIn =
            tr.x >= -margin && tr.y >= -margin && tr.x <= width + margin && tr.y <= height + margin;
          const brIn =
            br.x >= -margin && br.y >= -margin && br.x <= width + margin && br.y <= height + margin;
          if (!trIn || !brIn) continue;

          const tx = Math.min(width - 1, Math.max(0, Math.round(tr.x)));
          const ty = Math.min(height - 1, Math.max(0, Math.round(tr.y)));
          const voidBright = sampleBrightness(imageData, tx, ty, Math.max(6, avgSize / 4));
          const finderDark =
            (sampleBrightness(imageData, Math.round(tl.x), Math.round(tl.y), 3) +
              sampleBrightness(imageData, Math.round(bl.x), Math.round(bl.y), 3)) /
            2;

          if (![voidBright, finderDark, geom, sizeRatio].every(Number.isFinite)) continue;

          const verticality = down.y / d;
          const leftness = 1 - (tl.x + bl.x) / (2 * width);
          const geomScore = -Math.abs(geom - idealGeom) * 3;
          const sizeScore = sizeRatio * 20;
          const voidScore = voidBright - finderDark + (voidBright > 115 ? 18 : 0);
          const topRightBonus = tr.y < (tl.y + bl.y) / 2 ? 15 : -10;
          const score =
            voidScore + geomScore + sizeScore + verticality * 18 + leftness * 12 + topRightBonus;

          if (!Number.isFinite(score)) continue;
          if (!best || score > best.score) {
            best = { tl, bl, tr, br, down, right, score, geom, sizeRatio };
          }
        }
      }
    }
  }
  return best;
}

/** A warped matrix should show dark structure in the TL finder — reject blank/white crops. */
function warpLooksLikeQr(imageData) {
  if (!imageData?.data) return false;
  const { data, width: w } = imageData;
  let dark = 0;
  let n = 0;
  for (let my = 1; my <= 6; my++) {
    for (let mx = 1; mx <= 6; mx++) {
      const x = Math.floor(mx * CELL + CELL / 2);
      const y = Math.floor(my * CELL + CELL / 2);
      const i = (y * w + x) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (!Number.isFinite(lum)) continue;
      if (lum < 110) dark++;
      n++;
    }
  }
  if (!n) return false;
  const darkRatio = dark / n;
  return darkRatio > 0.25 && darkRatio < 0.92;
}

function moduleCorners(orient, geometry) {
  const { tl, down, right } = orient;
  const { grid, finderSpan } = geometry;
  const rU = { x: right.x / finderSpan, y: right.y / finderSpan };
  const dU = { x: down.x / finderSpan, y: down.y / finderSpan };
  const origin = {
    x: tl.x - FINDER_CENTER * rU.x - FINDER_CENTER * dU.x,
    y: tl.y - FINDER_CENTER * rU.y - FINDER_CENTER * dU.y,
  };
  return [
    origin,
    { x: origin.x + grid * rU.x, y: origin.y + grid * rU.y },
    { x: origin.x + grid * rU.x + grid * dU.x, y: origin.y + grid * rU.y + grid * dU.y },
    { x: origin.x + grid * dU.x, y: origin.y + grid * dU.y },
  ];
}

function paintMissingModules(ctx, missingModules, grid) {
  if (!missingModules?.modules?.length) return;
  const { row0, col0, rows, cols } = voidBounds(missingModules, grid);

  for (let r = 0; r < rows; r++) {
    const row = missingModules.modules[r];
    if (!row) continue;
    for (let c = 0; c < cols; c++) {
      ctx.fillStyle = row[c] ? "#000000" : "#ffffff";
      ctx.fillRect((col0 + c) * CELL, (row0 + r) * CELL, CELL, CELL);
    }
  }
}

/** Solve 8×8 (gaussian) for perspective H mapping src→dst (h22=1). Returns row-major 3×3. */
function getPerspectiveTransform(src, dst) {
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i++) {
    const { x: xs, y: ys } = src[i];
    const { x: xd, y: yd } = dst[i];
    A.push([xs, ys, 1, 0, 0, 0, -xs * xd, -ys * xd]);
    b.push(xd);
    A.push([0, 0, 0, xs, ys, 1, -xs * yd, -ys * yd]);
    b.push(yd);
  }
  const h = solveLinear(A, b);
  if (!h) return null;
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

function solveLinear(A, b) {
  const n = b.length;
  const M = A.map((row, i) => row.concat([b[i]]));
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null;
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }
    const div = M[col][col];
    for (let c = col; c <= n; c++) M[col][c] /= div;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row) => row[n]);
}

function applyH(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  if (Math.abs(w) < 1e-12) return null;
  return {
    x: (H[0] * x + H[1] * y + H[2]) / w,
    y: (H[3] * x + H[4] * y + H[5]) / w,
  };
}

function sampleBilinear(data, w, h, x, y) {
  if (x < 0 || y < 0 || x >= w - 1 || y >= h - 1) return [255, 255, 255, 255];
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const fx = x - x0;
  const fy = y - y0;
  const i00 = (y0 * w + x0) * 4;
  const i10 = (y0 * w + x1) * 4;
  const i01 = (y1 * w + x0) * 4;
  const i11 = (y1 * w + x1) * 4;
  const out = [0, 0, 0, 0];
  for (let c = 0; c < 4; c++) {
    const v0 = data[i00 + c] * (1 - fx) + data[i10 + c] * fx;
    const v1 = data[i01 + c] * (1 - fx) + data[i11 + c] * fx;
    out[c] = v0 * (1 - fy) + v1 * fy;
  }
  return out;
}

/** Inverse-map warp: dst square ← src quad via H_dst_to_src. */
function warpPerspective(imageData, srcCorners, outW, outH) {
  const dstCorners = [
    { x: 0, y: 0 },
    { x: outW, y: 0 },
    { x: outW, y: outH },
    { x: 0, y: outH },
  ];
  // H maps dst → src for inverse sampling
  const H = getPerspectiveTransform(dstCorners, srcCorners);
  if (!H) return null;

  const { data, width: sw, height: sh } = imageData;
  const out = new Uint8ClampedArray(outW * outH * 4);
  for (let y = 0; y < outH; y++) {
    for (let x = 0; x < outW; x++) {
      const p = applyH(H, x + 0.5, y + 0.5);
      const rgba = p ? sampleBilinear(data, sw, sh, p.x, p.y) : [255, 255, 255, 255];
      const o = (y * outW + x) * 4;
      out[o] = rgba[0];
      out[o + 1] = rgba[1];
      out[o + 2] = rgba[2];
      out[o + 3] = 255;
    }
  }
  return new ImageData(out, outW, outH);
}

/**
 * Map a point from the displayed element into bitmap (video/canvas) pixels.
 * fit: "cover" | "contain" | "fill" (fill = CSS w-full h-auto with matching aspect)
 */
function displayToBitmap(sx, sy, dispW, dispH, bmpW, bmpH, fit = "fill") {
  if (fit === "fill") {
    return { x: (sx / dispW) * bmpW, y: (sy / dispH) * bmpH };
  }
  const scale =
    fit === "contain"
      ? Math.min(dispW / bmpW, dispH / bmpH)
      : Math.max(dispW / bmpW, dispH / bmpH);
  const shownW = bmpW * scale;
  const shownH = bmpH * scale;
  const ox = (dispW - shownW) / 2;
  const oy = (dispH - shownH) / 2;
  return { x: (sx - ox) / scale, y: (sy - oy) / scale };
}

/**
 * Yellow guide square in display space → four corners in bitmap pixels.
 * Mirrors MatrixGuideOverlay: flex center + p-[7%] (padding % = of width) + aspect-square.
 * SVG content rect is inset ~1% (viewBox rect x=1 y=1 w=98).
 */
export function guideCornersInBitmap(dispW, dispH, bmpW, bmpH, fit = "fill") {
  const pad = 0.07 * dispW;
  const availW = dispW - 2 * pad;
  const availH = dispH - 2 * pad;
  const side = Math.min(availW, availH);
  const left = pad + (availW - side) / 2;
  const top = pad + (availH - side) / 2;
  const inset = side * 0.01;
  const dispCorners = [
    { x: left + inset, y: top + inset },
    { x: left + side - inset, y: top + inset },
    { x: left + side - inset, y: top + side - inset },
    { x: left + inset, y: top + side - inset },
  ];
  return dispCorners.map((p) => displayToBitmap(p.x, p.y, dispW, dispH, bmpW, bmpH, fit));
}

function paintPatched(warped, missingModules, grid) {
  const side = warped.width;
  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(side, side)
      : Object.assign(document.createElement("canvas"), { width: side, height: side });
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.putImageData(warped, 0, 0);
  paintMissingModules(ctx, missingModules, grid);
  return ctx.getImageData(0, 0, side, side);
}

/** Crisp black/white render of the module grid, wrapped in a quiet zone. */
function renderModuleGrid(dark, grid) {
  const side = (grid + QUIET * 2) * CELL;
  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(side, side)
      : Object.assign(document.createElement("canvas"), { width: side, height: side });
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, side, side);
  ctx.fillStyle = "#000000";
  for (let my = 0; my < grid; my++) {
    for (let mx = 0; mx < grid; mx++) {
      if (!dark[my * grid + mx]) continue;
      ctx.fillRect((QUIET + mx) * CELL, (QUIET + my) * CELL, CELL, CELL);
    }
  }
  return ctx.getImageData(0, 0, side, side);
}

/**
 * Primary path: warp a region around the on-screen guide, snap the module grid
 * onto the QR's spec-fixed patterns, paint the void, and re-render crisp with a
 * quiet zone. Finder detection in the raw frame is not required — the big QR is
 * intentionally broken, but its surviving TL/BL finders, timing lines and
 * alignment pattern are enough to pin the sampling grid.
 *
 * @param {ImageData} imageData full camera frame
 * @param {{grid:number, missingModules:{row0:number,col0:number,rows:number,cols:number,modules:number[][]}}} matrix
 * @param {number} dispW canvas element clientWidth
 * @param {number} dispH canvas element clientHeight
 */
export function reconstituteFromGuide(imageData, matrix, dispW, dispH, fit = "fill") {
  const missingModules = matrix?.missingModules;
  if (!missingModules?.modules?.length || !dispW || !dispH) {
    return { patched: null, corners: null, warpOk: false };
  }
  const geometry = symbolGeometry(matrix.grid);
  const corners = guideCornersInBitmap(dispW, dispH, imageData.width, imageData.height, fit);
  const searchQuad = shrinkQuad(corners, -(SEARCH_MARGIN * 2) / geometry.grid);
  const side = (geometry.grid + SEARCH_MARGIN * 2) * CELL;
  const warped = warpPerspective(imageData, searchQuad, side, side);
  if (!warped) return { patched: null, corners, warpOk: false };

  const gray = toGrayscale(warped);
  const thr = otsuThreshold(gray);
  const plan = buildSamplePlan(missingModules, geometry);
  const fitted = searchModuleGrid(gray, side, side, thr, plan, SEARCH_MARGIN * CELL, CELL);
  const dark = sampleGridDark(
    gray,
    side,
    side,
    thr,
    fitted.originX,
    fitted.originY,
    fitted.pitch,
    geometry.grid
  );

  applyMissingModules(dark, missingModules, geometry.grid);
  return {
    patched: renderModuleGrid(dark, geometry.grid),
    corners,
    warpOk: true,
    anchorMatch: fitted.match,
    zoom: fitted.pitch / CELL,
    looksLikeQr: fitted.match >= 0.95,
    thr,
  };
}

function shrinkQuad(quad, frac) {
  if (!frac) return quad;
  const cx = (quad[0].x + quad[2].x) / 2;
  const cy = (quad[0].y + quad[2].y) / 2;
  return quad.map((p) => ({
    x: cx + (p.x - cx) * (1 - frac),
    y: cy + (p.y - cy) * (1 - frac),
  }));
}

/**
 * Finder-based analyze (debug / optional refine). Primary unlock uses reconstituteFromGuide.
 */
export function analyzeFrame(imageData, matrix) {
  // Finder HUD is a QR pattern. Data Matrix pose comes from the L, which the
  // guide fitter already uses; there is no 1:1:3:1:1 finder to mark.
  if (matrix.grid === 36) {
    return { patched: null, centers: [], orient: null, thr: 0 };
  }
  const geometry = qrGeometry(matrix.grid);
  const { imageData: scan, scale } = downscaleForScan(imageData, 640);
  const { centers: rawCenters, thr } = findFinderCenters(scan);
  const centers = rawCenters.map((c) => ({
    x: c.x / scale,
    y: c.y / scale,
    size: c.size / scale,
    n: c.n,
  }));
  const orient = resolveTlBl(imageData, centers, geometry);
  if (!orient || !matrix.missingModules) {
    return { patched: null, centers, orient, thr, scale };
  }

  const corners = moduleCorners(orient, geometry);
  const side = geometry.grid * CELL;
  const warped = warpPerspective(imageData, corners, side, side);
  if (!warped || !warpLooksLikeQr(warped)) {
    return { patched: null, centers, orient, thr, scale, warpOk: false };
  }

  const patched = paintPatched(warped, matrix.missingModules, geometry.grid);
  return { patched, centers, orient, thr, scale, warpOk: true };
}

/**
 * @param {ImageData} imageData camera frame
 * @param {{grid:number, missingModules:object}} matrix outer matrix size + server patch JSON
 * @returns {ImageData|null} canonical patched matrix, or null if anchors not found
 */
export function reconstituteMatrix(imageData, matrix) {
  return analyzeFrame(imageData, matrix).patched;
}
